/**
 * Pure structural diff between two taxonomy version snapshots.
 *
 * Versions are immutable snapshots that carry their full `fields`/`rules`, and
 * field/rule IDs are preserved when a version is branched (copied forward), so a
 * "what changed" comparison is a client-side set-diff keyed by those IDs — no
 * backend call needed. Typically the `newVersion` is compared against its
 * `parentVersion` (the version it branched from), which yields exactly the edits
 * made in that version.
 */
import type {
  LinkedSchemaConfig,
  TaxonomyField,
  TaxonomyFieldGroup,
  TaxonomyListRef,
  TaxonomyOutput,
  TaxonomyRule,
  TaxonomyVersion,
} from '../model/types';
import {
  TAXONOMY_FIELD_TYPE_LABELS,
  TAXONOMY_TEXT_TRANSFORM_OPTIONS,
} from '../model/field-type-labels';
import { taxonomyLevelLabel } from '../model/constants';
import { DEFAULT_FIELD_DELIMITER } from '@inference.engine/taxonomy/web';
import { i18n } from '@/shared/i18n';
import {
  describeBranchAction,
  describeConditionGroup,
  getRuleBranches,
  withLiteralValues,
} from './rules';
import { patternText } from './patternText';

// Getters, not values: a resolved label would freeze at import.
const GROUP_LABELS: Record<TaxonomyFieldGroup, string> = {
  get media() {
    return i18n.t('knowledge:taxonomy.diffVersions.media');
  },
  get content() {
    return i18n.t('knowledge:taxonomy.diffVersions.content');
  },
};

/** One changed value within a dropdown field's value/code list. */
export interface ValueDiff {
  kind: 'added' | 'removed' | 'code_changed';
  value: string;
  /** Code for added/removed. */
  code?: string;
  oldCode?: string;
  newCode?: string;
}

interface Cells {
  old: string;
  new: string;
}

/** One changed attribute of a modified field (e.g. Required: No → Yes). */
export type AttrChange = { label: string } & Cells;

export interface FieldDiff {
  id: string;
  name: string;
  group: TaxonomyFieldGroup;
  /** Only populated for modified fields. */
  attrChanges: AttrChange[];
  valueChanges: ValueDiff[];
}

export interface RuleDiff {
  id: string;
  summary: string;
  /** Only populated for modified rules. */
  changes: string[];
}

export type PropChange = { label: string } & Cells;

export interface VersionDiff {
  fieldsAdded: FieldDiff[];
  fieldsRemoved: FieldDiff[];
  fieldsModified: FieldDiff[];
  rulesAdded: RuleDiff[];
  rulesRemoved: RuleDiff[];
  rulesModified: RuleDiff[];
  propChanges: PropChange[];
  /** Field-level tallies for a compact summary badge. */
  counts: { added: number; removed: number; modified: number };
  totalChanges: number;
  isEmpty: boolean;
}

type NameOf = (id: string) => string;

const DASH = '—';

/**
 * NOT trimmed: a single space is a legal `fieldDelimiter`, so treating
 * whitespace as unset made removing one invisible and setting one read as "—".
 */
const orDash = (v: string | null | undefined) => (v == null || v === '' ? DASH : v);

/** Quoted, so a delimiter that IS whitespace is visible rather than a blank cell. */
const quotedOrDash = (v: string | null | undefined) => (v == null || v === '' ? DASH : `"${v}"`);

const yesNo = (v: boolean | undefined) =>
  v ? i18n.t('knowledge:taxonomy.diffVersions.yes') : i18n.t('knowledge:taxonomy.diffVersions.no');

/**
 * Serialisation used to DECIDE a change rather than to show one.
 *
 * Empty containers normalise to absent: the settings composer writes
 * `trackingCode: { pattern: [] }` unconditionally on save, so an emptied
 * composer would otherwise differ from a version that never had the key and
 * report a change with two identical "—" cells.
 */
const structural = (v: unknown): string => {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.length === 0 ? '' : JSON.stringify(v);
  if (typeof v === 'object') {
    const compacted = Object.fromEntries(
      Object.entries(v as Record<string, unknown>).filter(([, value]) => structural(value) !== ''),
    );
    return Object.keys(compacted).length === 0 ? '' : JSON.stringify(compacted);
  }
  return JSON.stringify(v);
};

/** Ordered ids, for the properties where ORDER is the content. */
const idOrder = (items: readonly { id: string }[]) => items.map((i) => i.id).join(' ');

const sortedLimits = (v: TaxonomyVersion | null) =>
  [...(v?.settings.channelLimits ?? [])].sort((a, b) => a.platform.localeCompare(b.platform));

/**
 * A multi-select whose ORDER carries no meaning, compared in a stable order.
 *
 * `MultiSelectDropdown` re-adds an option with `[...value, option]`, so
 * unticking and re-ticking the same entry moves it to the end. Without this the
 * diff reports "NAMER, EMEA → EMEA, NAMER" — a change nobody made. Same failure
 * `sortedLimits` above exists for, and the same comparator, so two lists that
 * differ only in accent placement sort the same way in both.
 */
const sortedList = (items: readonly string[] | undefined) =>
  [...(items ?? [])].sort((a, b) => a.localeCompare(b));

/** Appends what changed to the new side, for edits a per-side label cannot show. */
function withNotes(oldText: string, newText: string, notes: string[]): Cells {
  return notes.length
    ? {
        old: oldText,
        new: i18n.t('knowledge:taxonomy.diffVersions.withNotes', {
          text: newText,
          notes: notes.join(', '),
        }),
      }
    : { old: oldText, new: newText };
}

function patternCell(pattern: TaxonomyField['pattern'], nameOf: NameOf): string {
  return pattern?.length ? patternText(pattern, nameOf) : DASH;
}

function listRefLabel(f: TaxonomyField): string {
  if (!f.listRef?.listId) return DASH;
  return i18n.t(
    f.listRef.listVersionId
      ? 'knowledge:taxonomy.diffVersions.linkedListPinned'
      : 'knowledge:taxonomy.diffVersions.linkedListFollowing',
  );
}

function linkCell(link: LinkedSchemaConfig | undefined): string {
  if (!link) return DASH;
  const what =
    link.pullKind === 'field'
      ? link.pullSource === 'value'
        ? i18n.t('knowledge:taxonomy.linkedSchema.fieldValue')
        : i18n.t('knowledge:taxonomy.linkedSchema.field')
      : i18n.t('knowledge:taxonomy.linkedSchema.trackingCode');
  return link.includeInName
    ? i18n.t('knowledge:taxonomy.diffVersions.linkIncludedInName', { what })
    : what;
}

function linkNotes(a: LinkedSchemaConfig | undefined, b: LinkedSchemaConfig | undefined) {
  const notes: string[] = [];
  if ((a?.targetSchemaId ?? null) !== (b?.targetSchemaId ?? null))
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.differentSchema'));
  if ((a?.pullFieldId ?? null) !== (b?.pullFieldId ?? null))
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.differentField'));
  return notes;
}

function listRefNotes(
  a: TaxonomyListRef | null | undefined,
  b: TaxonomyListRef | null | undefined,
) {
  const notes: string[] = [];
  if (a?.listId !== b?.listId) notes.push(i18n.t('knowledge:taxonomy.diffVersions.differentList'));
  if ((a?.listVersionId ?? null) !== (b?.listVersionId ?? null))
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.differentListVersion'));
  if (a?.valueColumnId !== b?.valueColumnId)
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.valueColumnChanged'));
  if ((a?.codeColumnId ?? null) !== (b?.codeColumnId ?? null))
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.codeColumnChanged'));
  if ((a?.pinnedBy ?? null) !== (b?.pinnedBy ?? null))
    notes.push(i18n.t('knowledge:taxonomy.diffVersions.pinSourceChanged'));
  return notes;
}

function outputCell(output: TaxonomyOutput, nameOf: NameOf, fallbackDelimiter: string): string {
  const flags = [
    output.isPrimary ? i18n.t('knowledge:taxonomy.diffVersions.outputPrimary') : null,
    output.requireUnique ? i18n.t('knowledge:taxonomy.diffVersions.outputUnique') : null,
    output.locked ? i18n.t('knowledge:taxonomy.diffVersions.outputLocked') : null,
    taxonomyLevelLabel(output.level) ?? null,
  ].filter((f): f is string => f !== null);
  const formula = patternText(output.parts, nameOf, output.delimiter ?? fallbackDelimiter);
  const text = i18n.t('knowledge:taxonomy.diffVersions.outputFormula', {
    name: output.name,
    formula: formula || DASH,
  });
  return flags.length
    ? i18n.t('knowledge:taxonomy.diffVersions.outputWithFlags', { text, flags: flags.join(', ') })
    : text;
}

interface FieldAttr {
  /** Property on `TaxonomyField`; `diffVersions.test.ts` keys its cases by this. */
  key: string;
  label: () => string;
  render: (field: TaxonomyField, nameOf: NameOf) => string;
  /**
   * What decides "changed", where a readable label would hide a real edit:
   * `listRef` displays as "Linked list (pinned)" whichever column it points at,
   * so re-pointing it must be compared structurally or it reads as no change.
   */
  compare?: (field: TaxonomyField) => string;
  /** Readable cells for when `render` gives the same text on both sides of a real change. */
  explain?: (oldF: TaxonomyField, newF: TaxonomyField, nameOf: NameOf) => Cells;
}

/**
 * Every field attribute a version diff reports.
 *
 * A table rather than a chain of `if`s because the defect it replaced WAS drift:
 * the chain compared five of the twenty-odd attributes a field carries, so
 * binding a shared list, tightening a validation pattern or giving a Variable
 * its first pattern part all reported "No changes" — on the badge someone reads
 * precisely to find out whether a published snapshot changed.
 *
 * Deliberately absent: `id` (identity — it is the diff key), `values` (has its
 * own richer diff in `valueChanges`), and `valueWidth`/`codeWidth`, which are
 * persisted drag-resize widths. Reporting those would make the badge lie in the
 * other direction, turning an accidental column drag into a schema change.
 */
export const FIELD_ATTRS: readonly FieldAttr[] = [
  {
    key: 'name',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.name'),
    render: (f) => f.name,
  },
  {
    key: 'type',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.type'),
    render: (f) => TAXONOMY_FIELD_TYPE_LABELS[f.type],
    // By the wire value, not the label: these types are a hand-written mirror of
    // the API's, so a type the mirror has not caught up with labels as
    // `undefined` on BOTH sides and the change would vanish.
    compare: (f) => f.type,
  },
  {
    key: 'group',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.section'),
    render: (f) => GROUP_LABELS[f.group],
    compare: (f) => f.group,
  },
  {
    key: 'required',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.required'),
    render: (f) => yesNo(f.required),
  },
  {
    key: 'format',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.format'),
    render: (f) => orDash(f.format),
  },
  {
    key: 'description',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.description'),
    render: (f) => orDash(f.description),
  },
  {
    key: 'visible',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.visible'),
    // Absent means visible, so an explicit `false` has to read as a change.
    render: (f) => yesNo(f.visible ?? true),
  },
  {
    key: 'unique',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.unique'),
    render: (f) => yesNo(f.unique),
  },
  {
    key: 'defaultValue',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.defaultValue'),
    render: (f) => orDash(f.defaultValue),
  },
  {
    key: 'validationPattern',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.validationPattern'),
    render: (f) => orDash(f.validationPattern),
  },
  {
    key: 'transform',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.transform'),
    render: (f) =>
      /**
       * `?? 'none'` here rather than in a `compare`: absent and `'none'` are the
       * same instruction, and `EditFieldModal` seeds its state with
       * `field.transform ?? 'none'` and always writes it back — so opening a
       * legacy field and saving with no edits persists `'none'`, which would
       * otherwise report as a change nobody made. Resolving it in `render`
       * settles display and comparison together.
       */
      TAXONOMY_TEXT_TRANSFORM_OPTIONS.find((o) => o.value === (f.transform ?? 'none'))?.label ??
      orDash(f.transform),
  },
  {
    key: 'level',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.level'),
    // The label every other screen shows ("Ad Set"), not the stored `ad_set`.
    render: (f) => taxonomyLevelLabel(f.level) ?? DASH,
    compare: (f) => f.level ?? '',
  },
  {
    key: 'multiselect',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.multiselect'),
    render: (f) => yesNo(f.multiselect),
  },
  {
    key: 'libraryKey',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.libraryKey'),
    render: (f) => orDash(f.libraryKey),
  },
  {
    key: 'parentId',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.parent'),
    render: (f, nameOf) => (f.parentId ? nameOf(f.parentId) : DASH),
    // By id: two parents can share a name, and a rename is its own change.
    compare: (f) => f.parentId ?? '',
  },
  {
    key: 'listRef',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.linkedList'),
    render: listRefLabel,
    compare: (f) => structural(f.listRef),
    explain: (a, b) =>
      withNotes(listRefLabel(a), listRefLabel(b), listRefNotes(a.listRef, b.listRef)),
  },
  {
    key: 'pattern',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.pattern'),
    render: (f) =>
      f.pattern?.length
        ? i18n.t('knowledge:taxonomy.diffVersions.patternParts', { count: f.pattern.length })
        : DASH,
    compare: (f) => structural(f.pattern),
    explain: (a, b, nameOf) => ({
      old: patternCell(a.pattern, nameOf),
      new: patternCell(b.pattern, nameOf),
    }),
  },
  {
    key: 'link',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.link'),
    render: (f) => (f.link ? i18n.t('knowledge:taxonomy.diffVersions.linkedSchema') : DASH),
    compare: (f) => structural(f.link),
    explain: (a, b) => withNotes(linkCell(a.link), linkCell(b.link), linkNotes(a.link, b.link)),
  },
];

/**
 * The pair of cells to SHOW for a change we already decided is real.
 *
 * A friendly label often cannot express what moved — re-pointing a binding at a
 * different code column still reads "Linked list (follows latest)" on both
 * sides, and an edited output formula still reads as its unchanged name. A row
 * that says "X → X" is worse than no row, because it makes the diff look broken
 * exactly where it has just started working. So when the labels collide, show the
 * attribute's own explanation, and only if that collides too, the structure that
 * decided the change.
 */
function shownAs(
  oldText: string,
  newText: string,
  before: string,
  after: string,
  explained?: Cells,
): Cells {
  if (oldText !== newText) return { old: oldText, new: newText };
  if (explained && explained.old !== explained.new) return explained;
  return { old: before || DASH, new: after || DASH };
}

function fieldAttrChanges(oldF: TaxonomyField, newF: TaxonomyField, nameOf: NameOf): AttrChange[] {
  const changes: AttrChange[] = [];
  for (const attr of FIELD_ATTRS) {
    const before = attr.compare ? attr.compare(oldF) : attr.render(oldF, nameOf);
    const after = attr.compare ? attr.compare(newF) : attr.render(newF, nameOf);
    if (before === after) continue;
    changes.push({
      label: attr.label(),
      ...shownAs(
        attr.render(oldF, nameOf),
        attr.render(newF, nameOf),
        before,
        after,
        attr.explain?.(oldF, newF, nameOf),
      ),
    });
  }
  return changes;
}

function valueChanges(oldF: TaxonomyField, newF: TaxonomyField): ValueDiff[] {
  const oldByValue = new Map(oldF.values.map((v) => [v.value, v.code]));
  const newByValue = new Map(newF.values.map((v) => [v.value, v.code]));
  const diffs: ValueDiff[] = [];

  for (const v of newF.values) {
    if (!oldByValue.has(v.value)) {
      diffs.push({ kind: 'added', value: v.value, code: v.code });
    } else {
      const oldCode = oldByValue.get(v.value) ?? '';
      if (oldCode !== v.code) {
        diffs.push({ kind: 'code_changed', value: v.value, oldCode, newCode: v.code });
      }
    }
  }
  for (const v of oldF.values) {
    if (!newByValue.has(v.value)) {
      diffs.push({ kind: 'removed', value: v.value, code: v.code });
    }
  }
  return diffs;
}

function ruleSummary(rule: TaxonomyRule, nameOf: (id: string) => string): string {
  return getRuleBranches(rule)
    .map((b) =>
      withLiteralValues((standIns) => i18n.t('knowledge:taxonomy.rules.branchSummary', standIns), {
        when: describeConditionGroup(b.when, nameOf),
        then: describeBranchAction(b, nameOf),
      }),
    )
    .join('  •  ');
}

function ruleChanges(
  oldR: TaxonomyRule,
  newR: TaxonomyRule,
  nameOf: (id: string) => string,
): string[] {
  const oldSummary = ruleSummary(oldR, nameOf);
  const newSummary = ruleSummary(newR, nameOf);
  if (oldSummary !== newSummary) return [`${oldSummary}  →  ${newSummary}`];
  /**
   * The readable summary deciding the diff is the exact shape that let the field
   * comparison drift, so it gets the same structural backstop: anything the
   * prose does not mention still registers, even though it can only be reported
   * as "edited".
   */
  return structural(oldR) === structural(newR)
    ? []
    : [`${newSummary}  ${i18n.t('knowledge:taxonomy.diffVersions.edited')}`];
}

function joinList(list: string[]): string {
  return list.length ? list.join(', ') : DASH;
}

/**
 * What the version-level comparisons need beyond the two versions themselves.
 *
 * Only the order props use it, but it is threaded to every entry rather than
 * special-cased, so the loop below stays a plain walk over the table — the same
 * shape `FIELD_ATTRS` already has with its `nameOf`.
 */
interface DiffContext {
  /**
   * Ids present in BOTH versions. The order props compare only these, because
   * a list of ids necessarily changes when something is added or removed — so
   * comparing all of them reported every add and every delete a second time, as
   * a reordering. Intersecting first means an add cannot masquerade as a move.
   */
  commonFieldIds: ReadonlySet<string>;
  commonRuleIds: ReadonlySet<string>;
  /**
   * 1-based position among the COMMON items, in the old version's order, so a
   * permutation reads without ids.
   *
   * Ranked within the common set rather than by absolute old position: a version
   * that reorders AND removes would otherwise show a surviving pair numbered
   * "#2, #3" with no #1 anywhere on screen.
   */
  ordinalOf: (id: string) => string;
  /**
   * A column's name, made unambiguous.
   *
   * Names are what a reader wants to see, but they are not unique — duplicates
   * are only a WARNING in validation, and a blank name is not even that. Two
   * swapped columns sharing a name rendered identically on both sides, so the
   * structural fallback took over and printed two rows of uuids. A position
   * suffix is added only where the name does not stand alone.
   */
  fieldLabelOf: (id: string) => string;
  /** A field's name from either version, for patterns that reference added or removed fields. */
  nameOf: NameOf;
}

interface VersionProp {
  key: string;
  label: () => string;
  render: (version: TaxonomyVersion | null, ctx: DiffContext) => string;
  compare?: (version: TaxonomyVersion | null, ctx: DiffContext) => string;
  /** One version's readable text, for when `render` gives the same text on both sides of a real change. */
  explain?: (version: TaxonomyVersion | null, ctx: DiffContext) => string;
}

/**
 * Version-level properties the diff reports.
 *
 * `settings` is broken into its individual knobs rather than compared whole, so
 * a changed delimiter reads as "Field delimiter: _ → -" instead of two blobs of
 * JSON. Neither `settings` nor `outputs` was compared at all before, which is
 * why editing an output formula or the schema's tracking-code pattern reported
 * no change.
 */
const VERSION_PROPS: readonly VersionProp[] = [
  {
    key: 'description',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.description'),
    render: (v) => orDash(v?.description),
  },
  {
    key: 'market',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.markets'),
    render: (v) => joinList(sortedList(v?.market)),
  },
  {
    key: 'platform',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.platforms'),
    render: (v) => joinList(sortedList(v?.platform)),
  },
  {
    key: 'fieldOrder',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.columnOrder'),
    /**
     * Order is content here, not presentation: the columns are concatenated in
     * this sequence to build the name, so dragging one changes every generated
     * string. The field diff is keyed by id and cannot see it — the same blind
     * spot, one level up.
     *
     * `render` is filtered to the common ids as well as `compare`. Showing the
     * full list would read "Country, Brand → Brand, Country, Market" on a
     * reorder that also added a column, inviting the reader to blame this row
     * for the addition — which has its own row already.
     */
    render: (v, ctx) =>
      joinList(
        (v?.fields ?? [])
          .filter((f) => ctx.commonFieldIds.has(f.id))
          .map((f) => ctx.fieldLabelOf(f.id)),
      ),
    compare: (v, ctx) => idOrder((v?.fields ?? []).filter((f) => ctx.commonFieldIds.has(f.id))),
  },
  {
    key: 'ruleOrder',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.ruleOrder'),
    /**
     * Old-position ordinals — "#1, #2" → "#2, #1".
     *
     * Not the rule summaries: a multi-branch rule renders as a paragraph, and
     * this cell would repeat the whole rule list to say that two of them
     * swapped. Not a count either, which was the previous answer and is
     * identical on both sides, so `shownAs` fell through to the structural
     * comparison and printed two rows of raw UUIDs.
     *
     * Anchoring to the OLD version's positions is what makes that impossible
     * rather than merely unlikely: the ordinals are distinct across the common
     * ids, so whenever `compare` fires the two strings necessarily differ.
     */
    render: (v, ctx) =>
      joinList(
        (v?.rules ?? []).filter((r) => ctx.commonRuleIds.has(r.id)).map((r) => ctx.ordinalOf(r.id)),
      ),
    compare: (v, ctx) => idOrder((v?.rules ?? []).filter((r) => ctx.commonRuleIds.has(r.id))),
  },
  {
    key: 'settings.fieldDelimiter',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.fieldDelimiter'),
    render: (v) => quotedOrDash(v?.settings.fieldDelimiter),
  },
  {
    key: 'settings.subfieldDelimiter',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.subfieldDelimiter'),
    render: (v) => quotedOrDash(v?.settings.subfieldDelimiter),
  },
  {
    key: 'settings.channelLimits',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.channelLimits'),
    /**
     * Sorted, because the editor rebuilds the array as `[...others, edited]` —
     * so changing a limit and changing it back leaves a permuted array that
     * would report as a change with two identically-sorted-looking rows.
     */
    render: (v) => joinList(sortedLimits(v).map((c) => `${c.platform} ${c.limit}`)),
  },
  {
    key: 'settings.trackingCode',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.trackingCodePattern'),
    render: (v) => {
      const parts = v?.settings.trackingCode?.pattern ?? [];
      return parts.length
        ? i18n.t('knowledge:taxonomy.diffVersions.patternParts', { count: parts.length })
        : DASH;
    },
    compare: (v) => structural(v?.settings.trackingCode),
    explain: (v, ctx) => patternCell(v?.settings.trackingCode?.pattern, ctx.nameOf),
  },
  {
    key: 'outputs',
    label: () => i18n.t('knowledge:taxonomy.diffVersions.outputs'),
    render: (v) => joinList((v?.outputs ?? []).map((o) => o.name)),
    // By formula, not by name: editing an output's parts leaves the names equal.
    compare: (v) => structural(v?.outputs),
    explain: (v, ctx) =>
      joinList(
        (v?.outputs ?? []).map((o) =>
          outputCell(o, ctx.nameOf, v?.settings.fieldDelimiter ?? DEFAULT_FIELD_DELIMITER),
        ),
      ),
  },
];

/**
 * Diffs `oldVersion` → `newVersion`. Pass `null` for `oldVersion` to diff against
 * an empty baseline (e.g. the first version, where everything reads as added).
 */
export function diffVersions(
  oldVersion: TaxonomyVersion | null,
  newVersion: TaxonomyVersion,
): VersionDiff {
  const oldFields = oldVersion?.fields ?? [];
  const newFields = newVersion.fields;
  const oldById = new Map(oldFields.map((f) => [f.id, f]));
  const newById = new Map(newFields.map((f) => [f.id, f]));

  // Field/rule name lookup spans both versions so rule summaries resolve names
  // even for fields that were removed.
  const nameById = new Map<string, string>();
  for (const f of [...oldFields, ...newFields]) nameById.set(f.id, f.name);
  const nameOf = (id: string) =>
    nameById.get(id) ?? i18n.t('knowledge:taxonomy.diffVersions.field');

  const fieldsAdded: FieldDiff[] = [];
  const fieldsRemoved: FieldDiff[] = [];
  const fieldsModified: FieldDiff[] = [];

  for (const f of newFields) {
    if (!oldById.has(f.id)) {
      fieldsAdded.push({
        id: f.id,
        name: f.name,
        group: f.group,
        attrChanges: [],
        valueChanges: [],
      });
    } else {
      const oldF = oldById.get(f.id)!;
      const attrChanges = fieldAttrChanges(oldF, f, nameOf);
      const vChanges = valueChanges(oldF, f);
      if (attrChanges.length > 0 || vChanges.length > 0) {
        fieldsModified.push({
          id: f.id,
          name: f.name,
          group: f.group,
          attrChanges,
          valueChanges: vChanges,
        });
      }
    }
  }
  for (const f of oldFields) {
    if (!newById.has(f.id)) {
      fieldsRemoved.push({
        id: f.id,
        name: f.name,
        group: f.group,
        attrChanges: [],
        valueChanges: [],
      });
    }
  }

  // Rules
  const oldRules = oldVersion?.rules ?? [];
  const newRules = newVersion.rules;
  const oldRuleById = new Map(oldRules.map((r) => [r.id, r]));
  const newRuleById = new Map(newRules.map((r) => [r.id, r]));

  const rulesAdded: RuleDiff[] = [];
  const rulesRemoved: RuleDiff[] = [];
  const rulesModified: RuleDiff[] = [];

  for (const r of newRules) {
    if (!oldRuleById.has(r.id)) {
      rulesAdded.push({ id: r.id, summary: ruleSummary(r, nameOf), changes: [] });
    } else {
      const changes = ruleChanges(oldRuleById.get(r.id)!, r, nameOf);
      if (changes.length > 0) {
        rulesModified.push({ id: r.id, summary: ruleSummary(r, nameOf), changes });
      }
    }
  }
  for (const r of oldRules) {
    if (!newRuleById.has(r.id)) {
      rulesRemoved.push({ id: r.id, summary: ruleSummary(r, nameOf), changes: [] });
    }
  }

  // Properties
  const commonFieldIds = new Set(newFields.filter((f) => oldById.has(f.id)).map((f) => f.id));
  const commonRuleIds = new Set(newRules.filter((r) => oldRuleById.has(r.id)).map((r) => r.id));
  const ordinals = new Map(
    (oldVersion?.rules ?? [])
      .filter((r) => commonRuleIds.has(r.id))
      .map((r, i) => [r.id, `#${i + 1}`]),
  );
  const commonOldFields = (oldVersion?.fields ?? []).filter((f) => commonFieldIds.has(f.id));
  const nameUses = new Map<string, number>();
  for (const f of commonOldFields) nameUses.set(f.name, (nameUses.get(f.name) ?? 0) + 1);
  const fieldLabels = new Map(
    commonOldFields.map((f, i) => {
      const ambiguous = !f.name.trim() || (nameUses.get(f.name) ?? 0) > 1;
      return [f.id, ambiguous ? `${f.name || DASH} #${i + 1}` : f.name];
    }),
  );

  const ctx: DiffContext = {
    commonFieldIds,
    commonRuleIds,
    ordinalOf: (id) => ordinals.get(id) ?? '',
    fieldLabelOf: (id) => fieldLabels.get(id) ?? DASH,
    nameOf,
  };

  const propChanges: PropChange[] = [];
  for (const prop of VERSION_PROPS) {
    const before = prop.compare ? prop.compare(oldVersion, ctx) : prop.render(oldVersion, ctx);
    const after = prop.compare ? prop.compare(newVersion, ctx) : prop.render(newVersion, ctx);
    if (before === after) continue;
    propChanges.push({
      label: prop.label(),
      ...shownAs(
        prop.render(oldVersion, ctx),
        prop.render(newVersion, ctx),
        before,
        after,
        prop.explain && {
          old: prop.explain(oldVersion, ctx),
          new: prop.explain(newVersion, ctx),
        },
      ),
    });
  }

  const counts = {
    added: fieldsAdded.length,
    removed: fieldsRemoved.length,
    modified: fieldsModified.length,
  };
  const totalChanges =
    counts.added +
    counts.removed +
    counts.modified +
    rulesAdded.length +
    rulesRemoved.length +
    rulesModified.length +
    propChanges.length;

  return {
    fieldsAdded,
    fieldsRemoved,
    fieldsModified,
    rulesAdded,
    rulesRemoved,
    rulesModified,
    propChanges,
    counts,
    totalChanges,
    isEmpty: totalChanges === 0,
  };
}

/** The version `versionId` branched from, resolved to a concrete version (or null). */
export function previousVersion(
  versions: TaxonomyVersion[],
  versionId: string,
): TaxonomyVersion | null {
  const current = versions.find((v) => v.id === versionId);
  if (!current) return null;
  if (current.parentVersionId) {
    const parent = versions.find((v) => v.id === current.parentVersionId);
    if (parent) return parent;
  }
  // Fallback: the version with the highest number below this one.
  const lower = versions
    .filter((v) => v.versionNumber < current.versionNumber)
    .sort((a, b) => b.versionNumber - a.versionNumber);
  return lower[0] ?? null;
}
